import VirtualGallery from '../VirtualGallery';
import useLatestRead from '../../hooks/useLatestRead';
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { buildAssetUrl, generationsAPI, projectsAPI } from '../../services/api';
import { getImageModelLabel } from '../../services/imageModels';
import ClipboardIcon from '../ClipboardIcon';
import GenerationMenu from '../GenerationMenu/GenerationMenu';
import OptimizedImage, { getPreviewImagePath } from '../OptimizedImage';
import Timer from '../Timer/Timer';
import './ProjectView.css';

const ProjectView = () => {
  const { projectId } = useParams();
  const startRead = useLatestRead();
  const [project, setProject] = useState(null);
  const [generations, setGenerations] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const originalOverflow = useRef(document.body.style.overflow);
  const loadMoreRef = useRef(null);
  const [hasMoreGenerations, setHasMoreGenerations] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showMoreClicked, setShowMoreClicked] = useState(false);
  const BATCH_SIZE = 12;
  const ITEMS_PER_ROW = 4;
  const INITIAL_ROWS = 1;
  const INITIAL_LIMIT = ITEMS_PER_ROW * INITIAL_ROWS;

  useEffect(() => {
    setShowMoreClicked(false);
    loadProjectData();
    // Clear error placeholders from previous session on mount
    const savedPlaceholders = JSON.parse(localStorage.getItem('generationPlaceholders') || '[]');
    const projectPlaceholders = savedPlaceholders.filter(p =>
      !p.error && p.project_id === parseInt(projectId)
    );
    localStorage.setItem('generationPlaceholders', JSON.stringify(projectPlaceholders));
  }, [projectId]);

  const mergeGenerations = (current, incoming) => {
    const merged = new Map();
    [...current, ...incoming].forEach((generation) => {
      if (generation?.id) {
        merged.set(generation.id, generation);
      }
    });
    return Array.from(merged.values()).sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  };

  const loadProjectData = async ({ append = false, limit = INITIAL_LIMIT, offset = 0 } = {}) => {
    const read = startRead();
    if (!read.isCurrent()) return;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const requests = append
        ? [Promise.resolve(null), generationsAPI.getByProject(projectId, { limit, offset, signal: read.signal })]
        : [projectsAPI.getAll({ signal: read.signal }), generationsAPI.getByProject(projectId, { limit, offset, signal: read.signal })];
      const [projectsResponse, generationsResponse] = await Promise.all(requests);

      if (!read.isCurrent()) return;
      if (projectsResponse) {
        const currentProject = projectsResponse.data.find(
          (p) => p.id === parseInt(projectId)
        );
        setProject(currentProject);
      }
      const fetchedGenerations = generationsResponse.data || [];
      setGenerations((prev) => (append ? mergeGenerations(prev, fetchedGenerations) : fetchedGenerations));
      setHasMoreGenerations(fetchedGenerations.length === limit);
    } catch (error) {
      if (!read.isCurrent()) return;
      console.error('Failed to load project data:', error);
    } finally {
      if (read.isCurrent()) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this generation?')) return;

    try {
      await generationsAPI.delete(id);
      setSelectedGeneration(null);
      loadProjectData();
    } catch (error) {
      console.error('Failed to delete generation:', error);
    }
  };

  const handleToggleLibrary = async (generationId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await generationsAPI.toggleLibrary(generationId, newStatus);

      // Update local state optimistically
      setGenerations(prevGens =>
        prevGens.map(gen =>
          gen.id === generationId ? { ...gen, in_library: newStatus } : gen
        )
      );

      // Also update selectedGeneration if it's the one being toggled
      if (selectedGeneration && selectedGeneration.id === generationId) {
        setSelectedGeneration(prev => ({ ...prev, in_library: newStatus }));
      }
    } catch (error) {
      console.error('Failed to toggle library status:', error);
    }
  };

  const openModal = (generation) => {
    setSelectedGeneration(generation);
    setIsZoomed(false);
    setZoomLevel(1);
  };

  const closeModal = () => {
    setSelectedGeneration(null);
    setIsZoomed(false);
    setZoomLevel(1);
  };

  const handleCopyPrompt = async (prompt) => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
    } catch (error) {
      console.error('Failed to copy prompt:', error);
    }
  };

  const handlePlaceholdersCreated = (newPlaceholders) => {
    // Add start time to each placeholder for timer
    const placeholdersWithTimer = newPlaceholders.map(p => ({
      ...p,
      startTime: Date.now()
    }));
    setPlaceholders(prev => [...placeholdersWithTimer, ...prev]);
  };

  const handleGenerationUpdate = (placeholderId, result) => {
    if (result.success) {
      // Remove placeholder and add real generation
      setPlaceholders(prev => prev.filter(p => p.id !== placeholderId));
      if (result.data) {
        setGenerations(prev => mergeGenerations([result.data], prev));
      }
    } else {
      // Update placeholder with error
      setPlaceholders(prev =>
        prev.map(p =>
          p.id === placeholderId
            ? { ...p, error: result.error, retryStatus: null }
            : p
        )
      );
    }
  };

  const handleRetryStatus = (placeholderId, retryData) => {
    // Update placeholder with retry status
    setPlaceholders(prev =>
      prev.map(p =>
        p.id === placeholderId
          ? { ...p, retryStatus: retryData }
          : p
      )
    );
  };

  const handleDownloadGeneration = (gen) => {
    if (!gen?.image_path) return;
    const link = document.createElement('a');
    link.href = buildAssetUrl(gen.image_path);
    link.download = `generation-${gen.id}.${(gen.output_format || 'png').toLowerCase()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleZoom = () => {
    setIsZoomed((prev) => {
      const next = !prev;
      setZoomLevel(next ? 1.5 : 1);
      return next;
    });
  };

  const handleWheelZoom = (event) => {
    if (!isZoomed) return;
    event.preventDefault();
    const direction = event.deltaY > 0 ? -0.15 : 0.15;

    setZoomLevel((prev) => {
      const next = Math.min(4, Math.max(1, prev + direction));

      if (next <= 1.01) {
        setIsZoomed(false);
        return 1;
      }

      return next;
    });
  };

  useEffect(() => {
    if (selectedGeneration || isZoomed) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = originalOverflow.current;
    }

    return () => {
      document.body.style.overflow = originalOverflow.current;
    };
  }, [selectedGeneration, isZoomed]);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !showMoreClicked || !hasMoreGenerations || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadProjectData({ append: true, limit: BATCH_SIZE, offset: generations.length });
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.1 }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [generations.length, showMoreClicked, hasMoreGenerations, loadingMore]);

  const handleShowMore = () => {
    setShowMoreClicked(true);
    if (!loadingMore && hasMoreGenerations) {
      loadProjectData({ append: true, limit: BATCH_SIZE, offset: generations.length });
    }
  };

  if (loading) {
    return <div className="project-view">Loading...</div>;
  }

  if (!project) {
    return <div className="project-view">Project not found</div>;
  }

  return (
    <div className="project-view">
      <div className="project-header">
        <h1>{project.name}</h1>
        <p className="project-date">
          Created: {new Date(project.created_at).toLocaleDateString()}
        </p>
      </div>

      <div className="project-content">
        <div className="generation-section">
          <GenerationMenu
            projectId={projectId}
            project={project}
            onGenerationComplete={loadProjectData}
            onPlaceholdersCreated={handlePlaceholdersCreated}
            onGenerationUpdate={handleGenerationUpdate}
            onRetryStatus={handleRetryStatus}
          />
        </div>

        <div className="history-section">
          <h2>Project History</h2>
          {placeholders.length === 0 && generations.length === 0 ? (
            <p className="empty-message">No generations in this project yet</p>
          ) : (
            <VirtualGallery className="generations-grid">
              {/* Display placeholders first */}
              {placeholders.map((placeholder) => (
                <div key={placeholder.id} className={`generation-card ${placeholder.error ? 'error-card' : 'placeholder-card'} ${placeholder.retryStatus ? 'retrying-card' : ''}`}>
                  {placeholder.error ? (
                    <div className="generation-image error-content">
                      <div className="error-icon">⚠</div>
                      <div className="error-text">Error</div>
                    </div>
                  ) : (
                    <div className="generation-image placeholder-shimmer">
                      <div className="shimmer-content">
                        {placeholder.retryStatus ? (
                          <>
                            <div className="retry-status-text">
                              Error {placeholder.retryStatus.errorStatus}
                            </div>
                            <div className="retry-attempt-text">
                              Retry {placeholder.retryStatus.attempt}/{placeholder.retryStatus.maxAttempts}
                            </div>
                          </>
                        ) : (
                          <div className="generating-text">Generating...</div>
                        )}
                        {placeholder.startTime && (
                          <div className="timer-display">
                            <Timer startTime={placeholder.startTime} className="generating" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="generation-info">
                    <div className="generation-prompt-row">
                      <p className="generation-prompt">{placeholder.prompt}</p>
                      <button
                        type="button"
                        className="btn-copy-prompt"
                        onClick={() => handleCopyPrompt(placeholder.prompt)}
                        title="Copy prompt"
                        aria-label="Copy prompt"
                      >
                        <ClipboardIcon />
                      </button>
                    </div>
                    {placeholder.error && (
                      <p className="error-message-text">{placeholder.error}</p>
                    )}
                    <div className="generation-meta">
                      {placeholder.model && <span>Modèle: {getImageModelLabel(placeholder.model)}</span>}
                      <span>{placeholder.aspect_ratio}</span>
                      <span>{placeholder.resolution}</span>
                      <span>{placeholder.output_format.toUpperCase()}</span>
                    </div>
                    <p className="generation-date">
                      {new Date(placeholder.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}

              {/* Display actual generations */}
              {generations.map((gen, index) => (
                <div key={gen.id} className="generation-card">
                  <div className="generation-image" onClick={() => openModal(gen)}>
                    <OptimizedImage
                      src={getPreviewImagePath(gen)}
                      alt={gen.prompt}
                      eager={index < INITIAL_LIMIT}
                    />
                  </div>
                  <div className="generation-info">
                    <div className="generation-prompt-row">
                      <p className="generation-prompt">{gen.prompt}</p>
                      <button
                        type="button"
                        className="btn-copy-prompt"
                        onClick={() => handleCopyPrompt(gen.prompt)}
                        title="Copy prompt"
                        aria-label="Copy prompt"
                      >
                        <ClipboardIcon />
                      </button>
                    </div>
                    <div className="generation-meta">
                      {gen.model && <span>Modèle: {getImageModelLabel(gen.model)}</span>}
                      <span>{gen.aspect_ratio}</span>
                      <span>{gen.resolution}</span>
                      <span>{gen.output_format.toUpperCase()}</span>
                    </div>
                    <p className="generation-cost">Cost: €{parseFloat(gen.cost || 0).toFixed(2)}</p>
                    {gen.generation_time_ms && (
                      <p className="generation-time">
                        Generation time: <Timer startTime={0} endTime={gen.generation_time_ms} className="completed" />
                      </p>
                    )}
                    {gen.username && (
                      <p className="generation-user">
                        By: <span className="username">@{gen.username}</span>
                      </p>
                    )}
                    <p className="generation-date">
                      {new Date(gen.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              {!loading && showMoreClicked && hasMoreGenerations && (
                <div className="load-more-sentinel" ref={loadMoreRef} aria-hidden="true" />
              )}
            </VirtualGallery>
          )}
          {!loading && hasMoreGenerations && (
            <button type="button" className="btn-show-more" onClick={handleShowMore} disabled={loadingMore}>
              {loadingMore ? 'Loading...' : 'Show More'}
            </button>
          )}
        </div>
      </div>

      {selectedGeneration && (
        <div className="modal-overlay" onClick={closeModal}>
          <button className="modal-close" onClick={closeModal}>
            X
          </button>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-image-section">
              <OptimizedImage
                src={selectedGeneration.image_path}
                alt={selectedGeneration.prompt}
                className={`modal-image ${isZoomed ? 'zoomed' : ''}`}
                onClick={toggleZoom}
                onWheel={handleWheelZoom}
                style={{ transform: isZoomed ? `scale(${zoomLevel})` : 'none' }}
                eager
              />
            </div>
            <div className="modal-details-section">
              <button
                onClick={() => handleDownloadGeneration(selectedGeneration)}
                className="btn-modal-download"
              >
                Download
                <span className="btn-icon">⬇</span>
              </button>

              <button
                onClick={() => handleToggleLibrary(selectedGeneration.id, selectedGeneration.in_library)}
                className={selectedGeneration.in_library ? "btn-modal-library-remove" : "btn-modal-library-add"}
              >
                {selectedGeneration.in_library ? 'Remove from Prompt Library' : 'Add to Prompt Library'}
                <span className="btn-icon">{selectedGeneration.in_library ? '📚' : '➕'}</span>
              </button>

              <div className="modal-detail-group">
                <div className="modal-detail-label">Prompt</div>
                <div className="modal-detail-value modal-prompt-row">
                  <span>{selectedGeneration.prompt}</span>
                  <button
                    type="button"
                    className="btn-copy-prompt"
                    onClick={() => handleCopyPrompt(selectedGeneration.prompt)}
                    title="Copy prompt"
                    aria-label="Copy prompt"
                  >
                    <ClipboardIcon />
                  </button>
                </div>
              </div>

              <div className="modal-detail-group">
                <div className="modal-detail-label">Settings</div>
                <div className="modal-meta-tags">
                  {selectedGeneration.model && (
                    <span>Modèle: {getImageModelLabel(selectedGeneration.model)}</span>
                  )}
                  <span>{selectedGeneration.aspect_ratio}</span>
                  <span>{selectedGeneration.resolution}</span>
                  <span>{selectedGeneration.output_format.toUpperCase()}</span>
                </div>
              </div>

              {selectedGeneration.reference_images && selectedGeneration.reference_images.length > 0 && (
                <div className="modal-detail-group">
                  <div className="modal-detail-label">Reference Images</div>
                  <div className="modal-reference-images">
                    {selectedGeneration.reference_images.map((ref, idx) => (
                      <div key={idx} className="modal-reference-thumbnail" title={ref.reference_type === 'project' ? 'Project Reference' : 'Upload Reference'}>
                        <OptimizedImage
                          src={getPreviewImagePath(ref)}
                          alt={`Reference ${idx + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="modal-detail-group">
                <div className="modal-detail-label">Cost</div>
                <div className="modal-detail-value">€{parseFloat(selectedGeneration.cost || 0).toFixed(2)}</div>
              </div>

              {selectedGeneration.generation_time_ms && (
                <div className="modal-detail-group">
                  <div className="modal-detail-label">Generation Time</div>
                  <div className="modal-detail-value">
                    <Timer startTime={0} endTime={selectedGeneration.generation_time_ms} className="completed" />
                  </div>
                </div>
              )}

              {selectedGeneration.username && (
                <div className="modal-detail-group">
                  <div className="modal-detail-label">Created By</div>
                  <div className="modal-detail-value">
                    <span className="modal-username">@{selectedGeneration.username}</span>
                  </div>
                </div>
              )}

              <div className="modal-detail-group">
                <div className="modal-detail-label">Created</div>
                <div className="modal-detail-value">
                  {new Date(selectedGeneration.created_at).toLocaleString()}
                </div>
              </div>

              <button
                onClick={() => handleDelete(selectedGeneration.id)}
                className="btn-modal-delete"
              >
                Delete
                <span className="btn-icon">🗑</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ProjectPage() {
  const { projectId } = useParams();
  return <ProjectView key={projectId} />;
}
